import {myAccountActionTypes as types} from '../actions/actionTypes';
import {CountryCodes} from '../helpers/constants';
import {deepCopy} from '../helpers';

const initialState = {
  account: null,
  countryCodes: [
    ...new Set(
      CountryCodes.sort((x, y) => x.dial_code - y.dial_code)
        .sort(
          (x, y) => (x.dial_code === '+1' ? -1 : y.dial_code === '+1' ? 1 : 0),
        )
        .map(({dial_code}) => dial_code),
    ), // eslint-disable-line camelcase
  ],
  googleCalendars: [],
  googleLoadingText: '',
  googleUrl: null,
  loading: {
    account: true,
    google: false,
    googleAuthenticate: true,
    googleNewCalendar: false,
    password: false,
    profilePicture: false,
    subscription: true,
    timeZone: true,
    twoFactorStatus: false,
  },
  subscription: null,
  timeZones: [],
  twoFactorStatus: {
    allowDisable: true,
    is2FAEnabled: false,
    phoneAvailable: true,
    twoFactorMethod: null,
  },
  twoFactorStatusError: null,
  twoFactorPassword: {
    error: null,
    loading: false,
    lockoutMessage: null,
    lockoutSource: null,
    retryAfterSeconds: 0,
    setupToken: null,
  },
  twoFactorSetup: {
    bannerError: null,
    codeError: null,
    emailDisabled: false,
    error: null,
    expiresIn: null,
    hitFiveAttemptFlag: false,
    initiating: false,
    initiatingMethod: null,
    method: null,
    resendBlocked: false,
    resendCooldownNonce: 0,
    resendCooldownSeconds: 0,
    resendErrorMessage: null,
    resendSessionError: null,
    resendSuccessMessage: null,
    resending: false,
    sessionRestartRequired: false,
    sessionToken: null,
    smsDisabled: false,
    verifyAttemptLimitActive: false,
    verifyLockoutMessage: null,
    verifyRetryAfterSeconds: 0,
    verifying: false,
  },
  twoFactorDisable: {
    error: null,
    loading: false,
  },
};

export default function myAccount(state = initialState, action) {
  switch (action.type) {
    case types.ACCOUNT_LOADING:
    case types.TIMEZONE_LOADING:
    case types.TIMEZONE_LOAD_FAIL:
    case types.GOOGLE_URL_LOADING:
    case types.ACCOUNT_LOAD_ERROR:
    case types.UPDATE_ACCOUNT_ERROR:
    case types.SUBSCRIPTION_LOADING:
    case types.TIMEZONE_LOAD_SUCCESS:
    case types.GOOGLE_URL_LOAD_ERROR:
    case types.UPDATE_PASSWORD_ERROR:
    case types.UPDATE_ACCOUNT_SUCCESS:
    case types.UPDATE_ACCOUNT_LOADING:
    case types.UPLOAD_PROFILE_PICTURE:
    case types.REMOVE_PROFILE_PICTURE:
    case types.SUBSCRIPTION_LOAD_ERROR:
    case types.UPDATE_PASSWORD_LOADING:
    case types.UPDATE_PASSWORD_SUCCESS:
    case types.GOOGLE_DISCONNECT_ERROR:
    case types.GOOGLE_AUTHENTICATE_FAIL:
    case types.GOOGLE_DISCONNECT_LOADING:
    case types.GOOGLE_CALENDAR_LOAD_FAIL:
    case types.UPLOAD_PROFILE_PICTURE_ERROR:
    case types.SELECT_GOOGLE_CALENDAR.LOADING:
    case types.SELECT_GOOGLE_CALENDAR.COMPLETE: {
      const loading = deepCopy(state.loading);
      loading[action.field] = action.loading;
      return {...state, loading};
    }

    case types.TWO_FACTOR_STATUS_LOADING:
      return {
        ...state,
        twoFactorStatusError: null,
        loading: {
          ...state.loading,
          twoFactorStatus: true,
        },
      };

    case types.TWO_FACTOR_STATUS_SUCCESS:
      return {
        ...state,
        twoFactorStatus: action.twoFactorStatus,
        twoFactorStatusError: null,
        loading: {
          ...state.loading,
          twoFactorStatus: false,
        },
      };

    case types.TWO_FACTOR_STATUS_ERROR:
      return {
        ...state,
        twoFactorStatusError: action.error,
        loading: {
          ...state.loading,
          twoFactorStatus: false,
        },
      };

    case types.TWO_FACTOR_VERIFY_PASSWORD_LOADING:
      return {
        ...state,
        twoFactorPassword: {
          ...state.twoFactorPassword,
          error: null,
          loading: true,
        },
      };

    case types.TWO_FACTOR_VERIFY_PASSWORD_SUCCESS:
      return {
        ...state,
        twoFactorPassword: {
          ...state.twoFactorPassword,
          error: null,
          loading: false,
          lockoutMessage: null,
          lockoutSource: null,
          retryAfterSeconds: 0,
          setupToken: action.setupToken,
        },
      };

    case types.TWO_FACTOR_VERIFY_PASSWORD_ERROR:
      return {
        ...state,
        twoFactorPassword: {
          ...state.twoFactorPassword,
          error: action.error,
          loading: false,
          lockoutMessage: action.lockoutMessage,
          lockoutSource: action.lockoutSource || null,
          retryAfterSeconds: action.retryAfterSeconds || 0,
        },
      };

    case types.TWO_FACTOR_VERIFY_PASSWORD_CLEAR_FORM:
      return {
        ...state,
        twoFactorPassword: {
          ...state.twoFactorPassword,
          error: null,
          loading: false,
        },
      };

    case types.TWO_FACTOR_VERIFY_PASSWORD_LOCKOUT_RESTORED:
      return {
        ...state,
        twoFactorPassword: {
          ...state.twoFactorPassword,
          error: null,
          loading: false,
          lockoutMessage: action.lockoutMessage,
          lockoutSource: action.lockoutSource || null,
          retryAfterSeconds: action.retryAfterSeconds,
        },
      };

    case types.TWO_FACTOR_VERIFY_PASSWORD_LOCKOUT_CLEARED:
      return {
        ...state,
        twoFactorPassword: {
          ...state.twoFactorPassword,
          lockoutMessage: null,
          lockoutSource: null,
          retryAfterSeconds: 0,
        },
      };

    case types.TWO_FACTOR_VERIFY_PASSWORD_RESET:
      return {
        ...state,
        twoFactorPassword: {
          error: null,
          loading: false,
          lockoutMessage: null,
          lockoutSource: null,
          retryAfterSeconds: 0,
          setupToken: null,
        },
      };

    case types.TWO_FACTOR_SETUP_INITIATE_LOADING:
      return {
        ...state,
        twoFactorSetup: {
          ...(state.twoFactorSetup || initialState.twoFactorSetup),
          error: null,
          initiating: true,
          initiatingMethod: action.initiatingMethod,
        },
      };

    case types.TWO_FACTOR_SETUP_INITIATE_SUCCESS:
      return {
        ...state,
        twoFactorSetup: {
          ...(state.twoFactorSetup || initialState.twoFactorSetup),
          error: null,
          expiresIn: action.expiresIn,
          initiating: false,
          initiatingMethod: null,
          method: action.method,
          sessionToken: action.sessionToken,
        },
      };

    case types.TWO_FACTOR_SETUP_INITIATE_ERROR:
      return {
        ...state,
        twoFactorSetup: {
          ...(state.twoFactorSetup || initialState.twoFactorSetup),
          emailDisabled:
            action.disableEmail != null
              ? action.disableEmail
              : (state.twoFactorSetup && state.twoFactorSetup.emailDisabled) ||
                false,
          error: action.error,
          initiating: false,
          initiatingMethod: null,
          smsDisabled:
            action.disableSms != null
              ? action.disableSms
              : (state.twoFactorSetup && state.twoFactorSetup.smsDisabled) ||
                false,
        },
      };

    case types.TWO_FACTOR_SETUP_RESET:
      return {
        ...state,
        twoFactorSetup: {
          bannerError: null,
          codeError: null,
          emailDisabled: false,
          error: null,
          expiresIn: null,
          hitFiveAttemptFlag: false,
          initiating: false,
          initiatingMethod: null,
          method: null,
          resendBlocked: false,
          resendCooldownSeconds: 0,
          resendErrorMessage: null,
          resendSessionError: null,
          resendSuccessMessage: null,
          resending: false,
          sessionRestartRequired: false,
          sessionToken: null,
          smsDisabled: false,
          verifyAttemptLimitActive: false,
          verifyLockoutMessage: null,
          verifyRetryAfterSeconds: 0,
          verifying: false,
        },
      };

    case types.TWO_FACTOR_VERIFY_MODAL_PREPARED:
      return {
        ...state,
        twoFactorSetup: {
          ...(state.twoFactorSetup || initialState.twoFactorSetup),
        bannerError: null,
        codeError: null,
        hitFiveAttemptFlag: action.hitFiveAttemptFlag,
          resendBlocked: action.resendBlocked,
          resendCooldownNonce: action.resendCooldownNonce || 0,
          resendCooldownSeconds: action.resendCooldownSeconds,
          resendErrorMessage: action.resendErrorMessage,
          resendSessionError: null,
          resendSuccessMessage: null,
          sessionRestartRequired: false,
          verifyAttemptLimitActive: action.verifyAttemptLimitActive,
          verifyLockoutMessage: action.verifyLockoutMessage,
          verifyRetryAfterSeconds: action.verifyRetryAfterSeconds,
        },
      };

    case types.TWO_FACTOR_VERIFY_LOCKOUT_CLEARED:
      return {
        ...state,
        twoFactorSetup: {
          ...(state.twoFactorSetup || initialState.twoFactorSetup),
          hitFiveAttemptFlag: false,
          verifyAttemptLimitActive: false,
          verifyLockoutMessage: null,
          verifyRetryAfterSeconds: 0,
        },
      };

    case types.TWO_FACTOR_VERIFY_SETUP_LOADING:
      return {
        ...state,
        twoFactorSetup: {
          ...(state.twoFactorSetup || initialState.twoFactorSetup),
          bannerError: null,
          sessionRestartRequired: false,
          verifying: true,
        },
      };

    case types.TWO_FACTOR_VERIFY_SETUP_SUCCESS:
      return {
        ...state,
        twoFactorSetup: {
          ...(state.twoFactorSetup || initialState.twoFactorSetup),
        bannerError: null,
        codeError: null,
        sessionRestartRequired: false,
        verifying: false,
        },
      };

    case types.TWO_FACTOR_VERIFY_SETUP_ERROR:
      return {
        ...state,
        twoFactorSetup: {
          ...(state.twoFactorSetup || initialState.twoFactorSetup),
          bannerError: action.bannerError,
          codeError: action.codeError,
          hitFiveAttemptFlag:
            action.hitFiveAttemptFlag != null
              ? action.hitFiveAttemptFlag
              : (state.twoFactorSetup && state.twoFactorSetup.hitFiveAttemptFlag) ||
                false,
          resendSuccessMessage: null,
          sessionRestartRequired: action.sessionRestartRequired || false,
          verifying: false,
          verifyAttemptLimitActive:
            action.verifyAttemptLimitActive != null
              ? action.verifyAttemptLimitActive
              : (state.twoFactorSetup &&
                  state.twoFactorSetup.verifyAttemptLimitActive) ||
                false,
          verifyLockoutMessage: action.verifyLockoutMessage,
          verifyRetryAfterSeconds: action.verifyRetryAfterSeconds || 0,
        },
      };

    case types.TWO_FACTOR_RESEND_LOADING:
      return {
        ...state,
        twoFactorSetup: {
          ...(state.twoFactorSetup || initialState.twoFactorSetup),
          bannerError: null,
          resendSuccessMessage: null,
          sessionRestartRequired: false,
          resending: true,
        },
      };

    case types.TWO_FACTOR_RESEND_SUCCESS:
      return {
        ...state,
        twoFactorSetup: {
          ...(state.twoFactorSetup || initialState.twoFactorSetup),
          bannerError: null,
          codeError: null,
          expiresIn: action.expiresIn,
          resendBlocked: action.resendBlocked || false,
          resendCooldownNonce:
            action.resendCooldownNonce != null
              ? action.resendCooldownNonce
              : action.resendCooldownSeconds
                ? Date.now()
                : (state.twoFactorSetup &&
                    state.twoFactorSetup.resendCooldownNonce) ||
                  0,
          resendCooldownSeconds: action.resendCooldownSeconds,
          resendErrorMessage: action.resendErrorMessage || null,
          resendSessionError: null,
          resendSuccessMessage: action.resendSuccessMessage,
          resending: false,
          sessionRestartRequired: false,
          sessionToken: action.sessionToken,
          verifyAttemptLimitActive: action.verifyAttemptLimitActive || false,
          verifyLockoutMessage: action.verifyLockoutMessage || null,
          verifyRetryAfterSeconds: action.verifyRetryAfterSeconds || 0,
        },
      };

    case types.TWO_FACTOR_RESEND_ERROR:
      return {
        ...state,
        twoFactorSetup: {
          ...(state.twoFactorSetup || initialState.twoFactorSetup),
          bannerError: action.bannerError || action.error || null,
          codeError: action.codeError || null,
          resendBlocked:
            action.resendBlocked != null
              ? action.resendBlocked
              : (state.twoFactorSetup && state.twoFactorSetup.resendBlocked) ||
                false,
          resendCooldownNonce:
            action.resendCooldownNonce != null
              ? action.resendCooldownNonce
              : action.resendCooldownSeconds
                ? Date.now()
                : (state.twoFactorSetup &&
                    state.twoFactorSetup.resendCooldownNonce) ||
                  0,
          resendCooldownSeconds:
            action.resendCooldownSeconds != null
              ? action.resendCooldownSeconds
              : (state.twoFactorSetup &&
                  state.twoFactorSetup.resendCooldownSeconds) ||
                0,
          resendErrorMessage: action.resendErrorMessage || null,
          resendSessionError: null,
          resendSuccessMessage: null,
          resending: false,
          sessionRestartRequired: action.sessionRestartRequired || false,
        },
      };

    case types.TWO_FACTOR_DISABLE_LOADING:
      return {
        ...state,
        twoFactorDisable: {
          ...state.twoFactorDisable,
          error: null,
          loading: true,
        },
      };

    case types.TWO_FACTOR_DISABLE_SUCCESS:
      return {
        ...state,
        twoFactorDisable: {
          error: null,
          loading: false,
        },
      };

    case types.TWO_FACTOR_DISABLE_ERROR:
      return {
        ...state,
        twoFactorDisable: {
          error: action.error,
          loading: false,
        },
      };

    case types.TWO_FACTOR_DISABLE_RESET:
      return {
        ...state,
        twoFactorDisable: {
          ...initialState.twoFactorDisable,
        },
      };

    case types.GOOGLE_AUTHENTICATE_LOADING:
      return {
        ...state,
        googleLoadingText: 'Authenticating your Google account...',
        loading: {
          ...state.loading,
          googleAuthenticate: true,
        },
      };

    case types.GOOGLE_CALENDAR_LOADING:
      return {
        ...state,
        googleLoadingText: 'Loading your Google calendars...',
      };

    case types.GOOGLE_CALENDAR_LOAD_SUCCESS:
      return {
        ...state,
        googleCalendars: action.googleCalendars,
        loading: {
          ...state.loading,
          googleAuthenticate: false,
        },
      };

    case types.ACCOUNT_LOAD_SUCCESS:
      return {
        ...state,
        account: action.account,
        loading: {
          ...state.loading,
          account: false,
        },
      };

    case types.GOOGLE_URL_LOAD_SUCCESS:
      return {
        ...state,
        googleUrl: action.googleUrl,
        loading: {
          ...state.loading,
          google: false,
        },
      };

    case types.UPLOAD_PROFILE_PICTURE_SUCCESS:
      return {
        ...state,
        account: {
          ...state.account,
          profileImageUri: action.profilePicture,
        },
        loading: {
          ...state.loading,
          profilePicture: false,
        },
      };

    case types.REMOVE_PROFILE_PICTURE_SUCCESS:
      return {
        ...state,
        account: {
          ...state.account,
          profileImageUri: null,
        },
        loading: {
          ...state.loading,
          profilePicture: false,
        },
      };

    case types.SUBSCRIPTION_LOAD_SUCCESS:
      return {
        ...state,
        subscription: action.subscription,
        loading: {
          ...state.loading,
          subscription: false,
        },
      };

    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/reducers/myAccountReducer.js